import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useContentCtx } from "@/context/ContentContext";
import { deepMerge } from "@/context/ContentContext";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Save, ArrowLeft, ExternalLink, Plus, Trash2, ArrowUp, ArrowDown, Upload, History, RotateCcw, Eye, X, Image as ImageIcon, Settings2 } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Field, TextArea, ImageField, ListEditor, StringListEditor } from "@/components/admin/EditorControls";
import { DEFAULT_CONTENT } from "@/lib/siteDefaults";

const clone = (o) => JSON.parse(JSON.stringify(o));
const emptyBase = () => clone(DEFAULT_CONTENT);

export function SiteEditorPanel({ stickyTop = "top-16" }) {
  const { reload } = useContentCtx();
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [histKey, setHistKey] = useState(0);

  useEffect(() => {
    api.get("/content/draft").then((r) => setDraft(clone(deepMerge(DEFAULT_CONTENT, r.data || {})))).catch(() => setDraft(emptyBase()));
  }, []);

  const refreshDraft = async () => {
    const r = await api.get("/content/draft");
    setDraft(clone(deepMerge(DEFAULT_CONTENT, r.data || {})));
  };

  const up = (section, patch) => setDraft((d) => ({ ...d, [section]: { ...d[section], ...patch } }));
  const upList = (section, key, items) => setDraft((d) => ({ ...d, [section]: { ...d[section], [key]: items } }));
  const upGreeting = (patch) => setDraft((d) => ({ ...d, company: { ...d.company, greeting: { ...d.company.greeting, ...patch } } }));

  const saveDraft = async () => {
    setSaving(true);
    try {
      await api.put("/content/draft", draft);
      toast.success("下書きを保存しました");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const publish = async () => {
    setPublishing(true);
    try {
      await api.put("/content/draft", draft);
      await api.post("/content/publish");
      await reload();
      setHistKey((k) => k + 1);
      toast.success("公開しました");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setPublishing(false); }
  };

  if (!draft) {
    return <div className="py-20 grid place-items-center"><Loader2 className="animate-spin text-gold" /></div>;
  }

  const H = ({ children }) => <span className="font-jp text-base">{children}</span>;

  return (
    <div className="text-white">
      <div className={`flex flex-wrap items-center justify-end gap-2 mb-6 sticky ${stickyTop} z-20 bg-ink/80 backdrop-blur py-3 -mx-1 px-1`}>
        <a href="/?preview=1" target="_blank" rel="noreferrer" data-testid="preview-link" className="inline-flex items-center gap-1.5 rounded-full border border-white/20 px-4 py-2 text-sm text-white/70 hover:text-gold hover:border-gold/40 transition-colors"><Eye size={15} /> プレビュー</a>
        <button onClick={saveDraft} disabled={saving} data-testid="save-draft" className="inline-flex items-center gap-2 rounded-full border border-white/20 px-4 py-2 text-sm text-white hover:border-gold/50 transition-colors disabled:opacity-60">
          {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} 下書き保存
        </button>
        <button onClick={publish} disabled={publishing} data-testid="publish-content" className="inline-flex items-center gap-2 rounded-full bg-gold px-5 py-2 text-sm font-medium text-ink hover:bg-gold-light glow-cyan transition-colors disabled:opacity-60">
          {publishing ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />} 公開
        </button>
      </div>

      <main className="max-w-4xl mx-auto px-5 py-8 pb-32">
        <Accordion type="multiple" defaultValue={["hero"]} className="space-y-3">

          <AccordionItem value="site" className="border border-white/10 rounded-xl px-4 bg-white/[0.02]">
            <AccordionTrigger data-testid="acc-site"><H>サイト共通（ロゴ・タイトル）</H></AccordionTrigger>
            <AccordionContent className="space-y-4 pt-2">
              <Field label="サイトタイトル（ブラウザタブ）" value={draft.site.title} onChange={(v) => up("site", { title: v })} />
              <ImageField label="ロゴ画像" value={draft.site.logo} onChange={(v) => up("site", { logo: v })} testid="site-logo" />
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="hero" className="border border-white/10 rounded-xl px-4 bg-white/[0.02]">
            <AccordionTrigger data-testid="acc-hero"><H>① ホーム（ヒーロー）</H></AccordionTrigger>
            <AccordionContent className="space-y-4 pt-2">
              <Field label="上部ラベル" value={draft.hero.eyebrow} onChange={(v) => up("hero", { eyebrow: v })} />
              <div className="grid grid-cols-2 gap-3">
                <Field label="見出し 1行目" value={draft.hero.line1} onChange={(v) => up("hero", { line1: v })} />
                <Field label="見出し 2行目" value={draft.hero.line2} onChange={(v) => up("hero", { line2: v })} />
              </div>
              <TextArea label="本文" value={draft.hero.subtitle} onChange={(v) => up("hero", { subtitle: v })} />
              <div className="grid grid-cols-2 gap-3">
                <Field label="ボタン1 ラベル" value={draft.hero.cta1_label} onChange={(v) => up("hero", { cta1_label: v })} />
                <Field label="ボタン1 リンク" value={draft.hero.cta1_link} onChange={(v) => up("hero", { cta1_link: v })} />
                <Field label="ボタン2 ラベル" value={draft.hero.cta2_label} onChange={(v) => up("hero", { cta2_label: v })} />
                <Field label="ボタン2 リンク" value={draft.hero.cta2_link} onChange={(v) => up("hero", { cta2_link: v })} />
              </div>
              <ImageField label="背景画像" value={draft.hero.bg_image} onChange={(v) => up("hero", { bg_image: v })} testid="hero-bg" />
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="manifesto" className="border border-white/10 rounded-xl px-4 bg-white/[0.02]">
            <AccordionTrigger data-testid="acc-manifesto"><H>② 企業理念</H></AccordionTrigger>
            <AccordionContent className="space-y-4 pt-2">
              <Field label="ラベル" value={draft.manifesto.eyebrow} onChange={(v) => up("manifesto", { eyebrow: v })} />
              <ListEditor
                items={draft.manifesto.chapters}
                setItems={(items) => upList("manifesto", "chapters", items)}
                makeNew={() => ({ id: `m${Date.now()}`, no: "", title: "", body: "", detail: "" })}
                addLabel="理念を追加"
                renderItem={(it, on) => (
                  <div className="space-y-3">
                    <div className="grid grid-cols-4 gap-3">
                      <Field label="番号" value={it.no} onChange={(v) => on({ no: v })} />
                      <div className="col-span-3"><Field label="タイトル" value={it.title} onChange={(v) => on({ title: v })} /></div>
                    </div>
                    <TextArea label="要約" value={it.body} onChange={(v) => on({ body: v })} rows={2} />
                    <TextArea label="詳細（ポップアップ）" value={it.detail} onChange={(v) => on({ detail: v })} rows={4} />
                  </div>
                )}
              />
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="services" className="border border-white/10 rounded-xl px-4 bg-white/[0.02]">
            <AccordionTrigger data-testid="acc-services"><H>③ サービス</H></AccordionTrigger>
            <AccordionContent className="space-y-4 pt-2">
              <Field label="ラベル" value={draft.services.eyebrow} onChange={(v) => up("services", { eyebrow: v })} />
              <TextArea label="見出し（改行可）" value={draft.services.heading} onChange={(v) => up("services", { heading: v })} rows={2} />
              <ListEditor
                items={draft.services.cards}
                setItems={(items) => upList("services", "cards", items)}
                makeNew={() => ({ id: `s${Date.now()}`, icon: "Layers", tag: "", title: "", body: "", detail: "", image: "", points: [] })}
                addLabel="サービスを追加"
                renderItem={(it, on) => (
                  <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <Field label="タグ" value={it.tag} onChange={(v) => on({ tag: v })} />
                      <Field label="アイコン (Globe/CreditCard/Smartphone/ShieldCheck/Layers)" value={it.icon} onChange={(v) => on({ icon: v })} />
                    </div>
                    <Field label="タイトル" value={it.title} onChange={(v) => on({ title: v })} />
                    <TextArea label="要約" value={it.body} onChange={(v) => on({ body: v })} rows={2} />
                    <TextArea label="詳細" value={it.detail} onChange={(v) => on({ detail: v })} rows={4} />
                    <ImageField label="画像（任意）" value={it.image} onChange={(v) => on({ image: v })} />
                    <div><span className="block text-xs text-white/50 mb-1.5">特長リスト</span><StringListEditor items={it.points} setItems={(v) => on({ points: v })} placeholder="特長" addLabel="特長を追加" /></div>
                  </div>
                )}
              />
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="works" className="border border-white/10 rounded-xl px-4 bg-white/[0.02]">
            <AccordionTrigger data-testid="acc-works"><H>④ 開発実績</H></AccordionTrigger>
            <AccordionContent className="space-y-4 pt-2">
              <Field label="ラベル" value={draft.works.eyebrow} onChange={(v) => up("works", { eyebrow: v })} />
              <TextArea label="見出し（改行可）" value={draft.works.heading} onChange={(v) => up("works", { heading: v })} rows={2} />
              <TextArea label="説明文" value={draft.works.description} onChange={(v) => up("works", { description: v })} rows={2} />
              <div className="rounded-lg bg-black/20 border border-white/10 p-3 text-xs text-white/50">実績カードの追加・編集・並び替えは下の「開発実績カード管理」で行い、その場で保存されます（このページの「保存」とは別）。</div>
              <WorksManager />
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="company" className="border border-white/10 rounded-xl px-4 bg-white/[0.02]">
            <AccordionTrigger data-testid="acc-company"><H>⑤ 会社概要</H></AccordionTrigger>
            <AccordionContent className="space-y-5 pt-2">
              <Field label="ラベル" value={draft.company.eyebrow} onChange={(v) => up("company", { eyebrow: v })} />
              <TextArea label="見出し（改行可）" value={draft.company.heading} onChange={(v) => up("company", { heading: v })} rows={2} />
              <ImageField label="代表者写真" value={draft.company.portrait} onChange={(v) => up("company", { portrait: v })} testid="company-portrait" />

              <div><span className="block text-sm text-gold mb-2">会社情報（表）</span>
                <ListEditor items={draft.company.info} setItems={(items) => upList("company", "info", items)}
                  makeNew={() => ({ id: `i${Date.now()}`, label: "", value: "" })} addLabel="行を追加"
                  renderItem={(it, on) => (
                    <div className="grid grid-cols-3 gap-3">
                      <Field label="項目" value={it.label} onChange={(v) => on({ label: v })} />
                      <div className="col-span-2"><Field label="内容" value={it.value} onChange={(v) => on({ value: v })} /></div>
                    </div>
                  )} />
              </div>

              <div><span className="block text-sm text-gold mb-2">事業目的</span>
                <StringListEditor items={draft.company.purposes.map((p) => p.text)} setItems={(vals) => upList("company", "purposes", vals.map((t, i) => ({ id: `p${i}`, text: t })))} placeholder="事業目的" addLabel="目的を追加" />
              </div>

              <div className="rounded-xl border border-white/10 bg-black/20 p-4 space-y-3">
                <span className="block text-sm text-gold">代表挨拶（ポップアップ）</span>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="ラベル" value={draft.company.greeting.label} onChange={(v) => upGreeting({ label: v })} />
                  <Field label="氏名" value={draft.company.greeting.name} onChange={(v) => upGreeting({ name: v })} />
                </div>
                <Field label="見出し（1行）" value={draft.company.greeting.heading} onChange={(v) => upGreeting({ heading: v })} />
                <div><span className="block text-xs text-white/50 mb-1.5">本文（段落）</span><StringListEditor items={draft.company.greeting.paragraphs} setItems={(v) => upGreeting({ paragraphs: v })} placeholder="段落" addLabel="段落を追加" /></div>
                <div><span className="block text-xs text-white/50 mb-1.5">特長リスト</span><StringListEditor items={draft.company.greeting.points} setItems={(v) => upGreeting({ points: v })} placeholder="特長" addLabel="特長を追加" /></div>
                <div><span className="block text-xs text-white/50 mb-1.5">結び（段落）</span><StringListEditor items={draft.company.greeting.closing} setItems={(v) => upGreeting({ closing: v })} placeholder="結びの段落" addLabel="段落を追加" /></div>
                <Field label="署名" value={draft.company.greeting.signature} onChange={(v) => upGreeting({ signature: v })} />
              </div>

              <div className="rounded-xl border border-white/10 bg-black/20 p-4 space-y-3">
                <span className="block text-sm text-gold">ロゴ（ポップアップ）</span>
                <ImageField label="ロゴ画像" value={draft.company.logo_image} onChange={(v) => up("company", { logo_image: v })} />
                <TextArea label="ロゴ説明文" value={draft.company.logo_desc} onChange={(v) => up("company", { logo_desc: v })} rows={3} />
              </div>
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="contact" className="border border-white/10 rounded-xl px-4 bg-white/[0.02]">
            <AccordionTrigger data-testid="acc-contact"><H>⑥ お問い合わせ</H></AccordionTrigger>
            <AccordionContent className="space-y-4 pt-2">
              <Field label="ラベル" value={draft.contact.eyebrow} onChange={(v) => up("contact", { eyebrow: v })} />
              <TextArea label="見出し（改行可）" value={draft.contact.heading} onChange={(v) => up("contact", { heading: v })} rows={3} />
              <TextArea label="説明文" value={draft.contact.description} onChange={(v) => up("contact", { description: v })} rows={2} />
              <div><span className="block text-xs text-white/50 mb-1.5">相談内容の選択肢</span><StringListEditor items={draft.contact.services} setItems={(v) => up("contact", { services: v })} placeholder="選択肢" addLabel="選択肢を追加" /></div>
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="footer" className="border border-white/10 rounded-xl px-4 bg-white/[0.02]">
            <AccordionTrigger data-testid="acc-footer"><H>⑦ フッター</H></AccordionTrigger>
            <AccordionContent className="space-y-4 pt-2">
              <Field label="キャッチコピー" value={draft.footer.tagline} onChange={(v) => up("footer", { tagline: v })} />
              <div><span className="block text-xs text-white/50 mb-1.5">会社情報の行</span><StringListEditor items={draft.footer.lines} setItems={(v) => up("footer", { lines: v })} placeholder="行テキスト" addLabel="行を追加" /></div>
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="marquee" className="border border-white/10 rounded-xl px-4 bg-white/[0.02]">
            <AccordionTrigger data-testid="acc-marquee"><H>⑧ マーキー（流れる文字）</H></AccordionTrigger>
            <AccordionContent className="space-y-4 pt-2">
              <StringListEditor items={draft.marquee.items} setItems={(v) => up("marquee", { items: v })} placeholder="表示テキスト" addLabel="テキストを追加" />
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="history" className="border border-white/10 rounded-xl px-4 bg-white/[0.02]">
            <AccordionTrigger data-testid="acc-history"><H>変更履歴（バージョン）</H></AccordionTrigger>
            <AccordionContent className="pt-2">
              <HistoryPanel refreshKey={histKey} onRestored={refreshDraft} />
            </AccordionContent>
          </AccordionItem>

        </Accordion>
      </main>
    </div>
  );
}

// ---- Version history panel (shared: site editor accordion + dashboard) ----
export function HistoryPanel({ refreshKey = 0, onRestored }) {
  const { reload } = useContentCtx();
  const [versions, setVersions] = useState([]);

  const load = () => api.get("/content/versions").then((r) => setVersions(r.data)).catch(() => {});
  // eslint-disable-next-line
  useEffect(() => { load(); }, [refreshKey]);

  const restore = async (id) => {
    try {
      await api.post(`/content/versions/${id}/restore`);
      if (onRestored) await onRestored();
      await reload();
      await load();
      toast.success("以前の内容に戻しました");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div className="space-y-2" data-testid="history-panel">
      <p className="text-xs text-white/40 mb-2">「公開」するたびに履歴が残ります。復元すると即座にサイトへ反映されます。</p>
      {versions.length === 0 ? (
        <p className="text-sm text-white/40 py-4">まだ履歴はありません。</p>
      ) : versions.map((v, i) => (
        <div key={v.id} className="flex items-center justify-between rounded-lg border border-white/10 bg-black/20 px-4 py-3" data-testid={`version-${i}`}>
          <div className="flex items-center gap-3">
            <History size={15} className="text-white/40" />
            <span className="text-sm text-white/80">{new Date(v.created_at).toLocaleString("ja-JP")}</span>
            {i === 0 && <span className="text-[10px] font-mono text-gold border border-gold/30 rounded px-2 py-0.5">最新</span>}
            {v.restored_from && <span className="text-[10px] text-white/40">復元</span>}
          </div>
          <button onClick={() => restore(v.id)} data-testid={`restore-${i}`} className="inline-flex items-center gap-1.5 rounded-full border border-white/20 px-3 py-1.5 text-xs text-white/70 hover:text-gold hover:border-gold/40 transition-colors">
            <RotateCcw size={13} /> この内容に戻す
          </button>
        </div>
      ))}
    </div>
  );
}

// ---- Recruit editor panel (separate admin card; edits the recruit part of the site draft) ----
export function RecruitPanel() {
  const { reload } = useContentCtx();
  const [recruit, setRecruit] = useState(null);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [editPos, setEditPos] = useState(null); // { item, index }（index=-1 は新規）
  const [settingsOpen, setSettingsOpen] = useState(false);

  useEffect(() => {
    api.get("/content/draft")
      .then((r) => setRecruit(clone(deepMerge(DEFAULT_CONTENT, r.data || {})).recruit))
      .catch(() => setRecruit(clone(DEFAULT_CONTENT.recruit)));
  }, []);

  const save = async (publish) => {
    if (publish) setPublishing(true); else setSaving(true);
    try {
      const r = await api.get("/content/draft");
      const draft = clone(deepMerge(DEFAULT_CONTENT, r.data || {}));
      draft.recruit = recruit;
      await api.put("/content/draft", draft);
      if (publish) {
        await api.post("/content/publish");
        await reload();
        toast.success("公開しました");
      } else {
        toast.success("下書きを保存しました");
      }
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); setPublishing(false); }
  };

  const movePos = (i, d) => {
    const j = i + d;
    if (j < 0 || j >= recruit.positions.length) return;
    const next = [...recruit.positions];
    [next[i], next[j]] = [next[j], next[i]];
    setRecruit({ ...recruit, positions: next });
  };
  const delPos = (i) => setRecruit({ ...recruit, positions: recruit.positions.filter((_, x) => x !== i) });
  const savePos = (item) => {
    const next = [...recruit.positions];
    if (editPos.index === -1) next.push(item);
    else next[editPos.index] = item;
    setRecruit({ ...recruit, positions: next });
    setEditPos(null);
    toast.success("内容を反映しました。「下書き保存」または「公開」で確定してください");
  };

  if (!recruit) {
    return <div className="py-12 flex justify-center"><Loader2 className="animate-spin text-gold" /></div>;
  }

  return (
    <div className="max-w-4xl mx-auto space-y-3" data-testid="recruit-panel">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <p className="text-sm text-gold">採用情報の管理（{recruit.positions.length}件）</p>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            type="button"
            onClick={() => setSettingsOpen(true)}
            data-testid="recruit-settings-open"
            className="inline-flex items-center gap-1.5 rounded-lg border border-white/20 px-4 py-2 text-sm text-white/70 hover:text-gold hover:border-gold/50 transition-colors"
          >
            <Settings2 size={14} /> 基本設定
          </button>
          <button
            type="button"
            onClick={() => setEditPos({ item: { id: `r${Date.now()}`, title: "", type: "", location: "", body: "" }, index: -1 })}
            data-testid="recruit-pos-add"
            className="inline-flex items-center gap-2 rounded-lg bg-gold px-4 py-2 text-sm font-medium text-ink hover:bg-gold-light transition-colors"
          >
            <Plus size={15} /> 職種を追加
          </button>
        </div>
      </div>
      <div className="flex items-center justify-end gap-2 flex-wrap">
        <a href="/?preview=1" target="_blank" rel="noreferrer" data-testid="recruit-preview" className="inline-flex items-center gap-1.5 rounded-full border border-white/20 px-4 py-2 text-sm text-white/70 hover:text-gold hover:border-gold/50 transition-colors">
          <Eye size={14} /> プレビュー
        </a>
        <button onClick={() => save(false)} disabled={saving} data-testid="recruit-save" className="inline-flex items-center gap-2 rounded-full border border-gold/40 text-gold px-4 py-2 text-sm hover:bg-gold hover:text-ink transition-colors disabled:opacity-60">
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} 下書き保存
        </button>
        <button onClick={() => save(true)} disabled={publishing} data-testid="recruit-publish" className="inline-flex items-center gap-2 rounded-full bg-gold px-4 py-2 text-sm font-medium text-ink hover:bg-gold-light transition-colors disabled:opacity-60">
          {publishing ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />} 公開
        </button>
      </div>
      <p className="text-xs text-white/40">職種名をクリックすると編集画面がポップアップで開きます。並び替えは右の↑↓ボタンです。変更後は「下書き保存」または「公開」で確定してください。</p>

      {recruit.positions.length === 0 ? (
        <p className="py-10 text-center text-white/40 text-sm rounded-xl border border-dashed border-white/10" data-testid="recruit-positions-empty">まだ職種がありません。「職種を追加」から作成してください。</p>
      ) : (
        <div className="space-y-2" data-testid="recruit-positions-list">
          {recruit.positions.map((p, i) => (
            <div
              key={p.id || i}
              data-testid={`recruit-pos-${i}`}
              onClick={() => setEditPos({ item: { ...p }, index: i })}
              className="group flex items-center gap-4 rounded-xl border border-white/10 bg-black/20 p-3.5 cursor-pointer hover:border-gold/40 hover:bg-white/[0.04] transition-all"
            >
              <div className="min-w-0 flex-1">
                <p className="font-jp font-bold text-white truncate group-hover:text-gold transition-colors">{p.title || "（無題）"}</p>
                <p className="text-xs text-white/40 mt-0.5 truncate">{[p.type, p.location].filter(Boolean).join(" / ") || "　"}</p>
              </div>
              <div className="flex items-center gap-1 shrink-0" onClick={(e) => e.stopPropagation()}>
                <button type="button" onClick={() => movePos(i, -1)} disabled={i === 0} data-testid={`recruit-pos-up-${i}`} className="p-1.5 text-white/50 hover:text-gold disabled:opacity-30 transition-colors"><ArrowUp size={15} /></button>
                <button type="button" onClick={() => movePos(i, 1)} disabled={i === recruit.positions.length - 1} data-testid={`recruit-pos-down-${i}`} className="p-1.5 text-white/50 hover:text-gold disabled:opacity-30 transition-colors"><ArrowDown size={15} /></button>
                <button type="button" onClick={() => delPos(i)} data-testid={`recruit-pos-del-${i}`} className="p-1.5 text-white/50 hover:text-red-400 transition-colors"><Trash2 size={15} /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {settingsOpen && (
        <RecruitSettingsModal
          recruit={recruit}
          onSave={(r) => {
            setRecruit(r);
            setSettingsOpen(false);
            toast.success("内容を反映しました。「下書き保存」または「公開」で確定してください");
          }}
          onClose={() => setSettingsOpen(false)}
        />
      )}

      {editPos && (
        <RecruitPositionModal
          position={editPos.item}
          isNew={editPos.index === -1}
          onSave={savePos}
          onClose={() => setEditPos(null)}
        />
      )}
    </div>
  );
}

function RecruitSettingsModal({ recruit, onSave, onClose }) {
  const [r, setR] = useState({ ...recruit });
  const on = (patch) => setR({ ...r, ...patch });
  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/75 backdrop-blur-sm p-4" onClick={onClose} data-testid="recruit-settings-modal">
      <div className="w-full max-w-2xl rounded-2xl border border-white/10 bg-surface overflow-hidden flex flex-col max-h-[92vh]" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 sm:px-8 py-5 border-b border-white/10 shrink-0">
          <div className="min-w-0">
            <p className="text-[10px] tracking-[0.2em] text-gold/80 font-mono uppercase">Recruit Settings</p>
            <p className="font-jp font-bold text-lg text-white truncate mt-0.5">採用情報の基本設定</p>
          </div>
          <button type="button" onClick={onClose} data-testid="recruit-settings-close" className="text-white/40 hover:text-white transition-colors shrink-0"><X size={20} /></button>
        </div>
        <div className="px-6 sm:px-8 py-6 space-y-4 overflow-y-auto">
          <Field label="ラベル" value={r.eyebrow} onChange={(v) => on({ eyebrow: v })} testid="rs-eyebrow" />
          <TextArea label="見出し（改行可）" value={r.heading} onChange={(v) => on({ heading: v })} rows={2} testid="rs-heading" />
          <TextArea label="説明文" value={r.intro} onChange={(v) => on({ intro: v })} rows={3} testid="rs-intro" />
          <Field label="応募ボタン ラベル" value={r.cta_label} onChange={(v) => on({ cta_label: v })} testid="rs-cta-label" />
        </div>
        <div className="px-6 sm:px-8 py-4 border-t border-white/10 bg-black/20 flex items-center gap-3 shrink-0">
          <button type="button" onClick={() => onSave(r)} data-testid="recruit-settings-save" className="inline-flex items-center gap-2 rounded-lg bg-gold px-6 py-2.5 text-sm font-bold text-ink hover:bg-gold-light transition-colors">
            <Save size={15} /> 保存する
          </button>
          <button type="button" onClick={onClose} data-testid="recruit-settings-cancel" className="rounded-lg border border-white/15 px-5 py-2.5 text-sm text-white/70 hover:text-white transition-colors">
            キャンセル
          </button>
        </div>
      </div>
    </div>
  );
}

function RecruitPositionModal({ position, isNew, onSave, onClose }) {
  const [p, setP] = useState(position);
  const on = (patch) => setP({ ...p, ...patch });
  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/75 backdrop-blur-sm p-4" onClick={onClose} data-testid="recruit-pos-modal">
      <div className="w-full max-w-2xl rounded-2xl border border-white/10 bg-surface overflow-hidden flex flex-col max-h-[92vh]" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 sm:px-8 py-5 border-b border-white/10 shrink-0">
          <div className="min-w-0">
            <p className="text-[10px] tracking-[0.2em] text-gold/80 font-mono uppercase">{isNew ? "New Position" : "Edit Position"}</p>
            <p className="font-jp font-bold text-lg text-white truncate mt-0.5">{isNew ? "職種を追加" : (p.title || "職種を編集")}</p>
          </div>
          <button type="button" onClick={onClose} data-testid="recruit-pos-close" className="text-white/40 hover:text-white transition-colors shrink-0"><X size={20} /></button>
        </div>
        <div className="px-6 sm:px-8 py-6 space-y-4 overflow-y-auto">
          <Field label="職種名" value={p.title} onChange={(v) => on({ title: v })} testid="rp-title" />
          <div className="grid grid-cols-2 gap-3">
            <Field label="雇用形態" value={p.type} onChange={(v) => on({ type: v })} testid="rp-type" />
            <Field label="勤務地" value={p.location} onChange={(v) => on({ location: v })} testid="rp-location" />
          </div>
          <TextArea label="仕事内容" value={p.body} onChange={(v) => on({ body: v })} rows={4} testid="rp-body" />
        </div>
        <div className="px-6 sm:px-8 py-4 border-t border-white/10 bg-black/20 flex items-center gap-3 shrink-0">
          <button type="button" onClick={() => onSave(p)} data-testid="recruit-pos-save" className="inline-flex items-center gap-2 rounded-lg bg-gold px-6 py-2.5 text-sm font-bold text-ink hover:bg-gold-light transition-colors">
            <Plus size={15} /> {isNew ? "追加する" : "保存する"}
          </button>
          <button type="button" onClick={onClose} data-testid="recruit-pos-cancel" className="rounded-lg border border-white/15 px-5 py-2.5 text-sm text-white/70 hover:text-white transition-colors">
            キャンセル
          </button>
        </div>
      </div>
    </div>
  );
}

// ---- Works manager (works API-backed) ----
export function WorksManager() {
  const [works, setWorks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | work（新規は id なし）
  const emptyW = { title: "", category: "", year: "", summary: "", description: "", tech: [], image: "", url: "" };

  const load = () => {
    setLoading(true);
    api.get("/works").then((r) => setWorks(r.data)).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const saveWork = async (w) => {
    try {
      const payload = { ...w, tech: typeof w.tech === "string" ? w.tech.split(",").map((s) => s.trim()).filter(Boolean) : w.tech };
      if (w.id) await api.put(`/works/${w.id}`, payload);
      else await api.post("/works", payload);
      toast.success("保存しました");
      setEditing(null);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const del = async (id) => { try { await api.delete(`/works/${id}`); toast.success("削除しました"); load(); } catch (e) { toast.error("削除に失敗"); } };
  const move = async (i, d) => {
    const j = i + d; if (j < 0 || j >= works.length) return;
    const next = [...works]; [next[i], next[j]] = [next[j], next[i]]; setWorks(next);
    try { await api.put("/works-order", { ids: next.map((w) => w.id) }); } catch (e) { toast.error("並び替えに失敗"); }
  };

  if (loading) return <div className="py-6 flex justify-center"><Loader2 className="animate-spin text-gold" /></div>;

  return (
    <div className="space-y-3" data-testid="works-manager">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <p className="text-sm text-gold">開発実績カード管理</p>
        <button
          type="button"
          onClick={() => setEditing({ ...emptyW })}
          data-testid="work-add"
          className="inline-flex items-center gap-2 rounded-lg bg-gold px-4 py-2 text-sm font-medium text-ink hover:bg-gold-light transition-colors"
        >
          <Plus size={15} /> 新規追加
        </button>
      </div>
      <p className="text-xs text-white/40">タイトルをクリックすると編集画面がポップアップで開きます。並び替えは右の↑↓ボタンです。</p>

      {works.length === 0 ? (
        <p className="py-10 text-center text-white/40 text-sm" data-testid="works-empty">まだ実績がありません。「新規追加」から作成してください。</p>
      ) : (
        <div className="space-y-2" data-testid="works-list">
          {works.map((w, i) => (
            <div
              key={w.id}
              data-testid={`work-row-${w.id}`}
              onClick={() => setEditing(w)}
              className="group flex items-center gap-4 rounded-xl border border-white/10 bg-white/[0.02] p-3.5 cursor-pointer hover:border-gold/40 hover:bg-white/[0.04] transition-all"
            >
              {w.image ? (
                <img src={w.image} alt="" className="h-12 w-16 rounded-lg object-cover border border-white/10 shrink-0" />
              ) : (
                <div className="h-12 w-16 rounded-lg border border-white/10 bg-white/5 grid place-items-center shrink-0">
                  <ImageIcon size={16} className="text-white/20" />
                </div>
              )}
              <div className="min-w-0 flex-1">
                <p className="font-jp font-bold text-white truncate group-hover:text-gold transition-colors">{w.title || "（無題）"}</p>
                <p className="text-xs text-white/40 mt-0.5 truncate">{[w.category, w.year].filter(Boolean).join(" / ") || "　"}</p>
              </div>
              <div className="flex items-center gap-1 shrink-0" onClick={(e) => e.stopPropagation()}>
                <button type="button" onClick={() => move(i, -1)} disabled={i === 0} data-testid={`work-up-${w.id}`} className="p-1.5 text-white/50 hover:text-gold disabled:opacity-30 transition-colors"><ArrowUp size={15} /></button>
                <button type="button" onClick={() => move(i, 1)} disabled={i === works.length - 1} data-testid={`work-down-${w.id}`} className="p-1.5 text-white/50 hover:text-gold disabled:opacity-30 transition-colors"><ArrowDown size={15} /></button>
                <button type="button" onClick={() => del(w.id)} data-testid={`work-delete-${w.id}`} className="p-1.5 text-white/50 hover:text-red-400 transition-colors"><Trash2 size={15} /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <WorkEditModal
          work={editing}
          isNew={!editing.id}
          onSave={saveWork}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
}

function WorkEditModal({ work, isNew, onSave, onClose }) {
  const [w, setW] = useState({ ...work, tech: Array.isArray(work.tech) ? work.tech.join(", ") : work.tech || "" });
  const on = (patch) => setW({ ...w, ...patch });

  const SectionLabel = ({ children }) => (
    <p className="text-xs font-bold text-white/60 mb-3 flex items-center gap-2">
      <span className="h-px w-4 bg-gold/50" />{children}
    </p>
  );

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/75 backdrop-blur-sm p-4" onClick={onClose} data-testid="work-edit-modal">
      <div className="w-full max-w-3xl rounded-2xl border border-white/10 bg-surface overflow-hidden flex flex-col max-h-[92vh]" onClick={(e) => e.stopPropagation()}>
        {/* ヘッダー（画像プレビュー付き） */}
        <div className="flex items-center justify-between px-6 sm:px-8 py-5 border-b border-white/10 shrink-0">
          <div className="flex items-center gap-4 min-w-0">
            {w.image ? (
              <img src={w.image} alt="" className="h-14 w-20 rounded-lg object-cover border border-white/10 shrink-0" />
            ) : (
              <div className="h-14 w-20 rounded-lg border border-dashed border-white/15 bg-white/5 grid place-items-center shrink-0">
                <ImageIcon size={18} className="text-white/25" />
              </div>
            )}
            <div className="min-w-0">
              <p className="text-[10px] tracking-[0.2em] text-gold/80 font-mono uppercase">{isNew ? "New Work" : "Edit Work"}</p>
              <p className="font-jp font-bold text-lg text-white truncate mt-0.5">{isNew ? "実績を追加" : (w.title || "実績を編集")}</p>
            </div>
          </div>
          <button type="button" onClick={onClose} data-testid="work-edit-close" className="text-white/40 hover:text-white transition-colors shrink-0"><X size={20} /></button>
        </div>

        {/* 本文（スクロール） */}
        <div className="px-6 sm:px-8 py-6 space-y-7 overflow-y-auto">
          <section>
            <SectionLabel>基本情報</SectionLabel>
            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-2"><Field label="タイトル" value={w.title} onChange={(v) => on({ title: v })} testid="we-title" /></div>
              <Field label="年" value={w.year} onChange={(v) => on({ year: v })} testid="we-year" />
            </div>
            <div className="grid grid-cols-2 gap-3 mt-3">
              <Field label="カテゴリ" value={w.category} onChange={(v) => on({ category: v })} testid="we-category" />
              <Field label="サイトURL" value={w.url} onChange={(v) => on({ url: v })} testid="we-url" />
            </div>
          </section>

          <section>
            <SectionLabel>説明文</SectionLabel>
            <div className="space-y-3">
              <Field label="要約（カードに表示）" value={w.summary} onChange={(v) => on({ summary: v })} testid="we-summary" />
              <TextArea label="詳細（クリック時に表示）" value={w.description} onChange={(v) => on({ description: v })} rows={3} testid="we-description" />
            </div>
          </section>

          <section>
            <SectionLabel>技術・画像</SectionLabel>
            <div className="space-y-3">
              <Field label="技術スタック（カンマ区切り）" value={w.tech} onChange={(v) => on({ tech: v })} testid="we-tech" />
              <ImageField label="画像" value={w.image} onChange={(v) => on({ image: v })} />
            </div>
          </section>
        </div>

        {/* フッター（常時表示） */}
        <div className="px-6 sm:px-8 py-4 border-t border-white/10 bg-black/20 flex items-center gap-3 shrink-0">
          <button
            type="button"
            onClick={() => onSave({ ...w })}
            data-testid="work-save"
            className="inline-flex items-center gap-2 rounded-lg bg-gold px-6 py-2.5 text-sm font-bold text-ink hover:bg-gold-light transition-colors"
          >
            <Plus size={15} /> {isNew ? "追加する" : "保存する"}
          </button>
          <button type="button" onClick={onClose} data-testid="work-cancel" className="rounded-lg border border-white/15 px-5 py-2.5 text-sm text-white/70 hover:text-white transition-colors">
            キャンセル
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AdminSiteEditor() {
  const { user, ready } = useAuth();
  const navigate = useNavigate();
  useEffect(() => { if (ready && !user) navigate("/admin/login"); }, [ready, user, navigate]);
  if (!ready || !user) return <div className="min-h-screen bg-ink grid place-items-center"><Loader2 className="animate-spin text-gold" /></div>;
  return (
    <div className="min-h-screen bg-ink text-white">
      <header className="border-b border-white/10 sticky top-0 z-30 bg-black/70 backdrop-blur-2xl">
        <div className="max-w-4xl mx-auto px-5 h-16 flex items-center gap-3">
          <Link to="/admin" data-testid="back-dashboard" className="text-white/50 hover:text-gold transition-colors"><ArrowLeft size={18} /></Link>
          <span className="font-display text-lg font-extrabold tracking-tight">サイト編集</span>
        </div>
      </header>
      <SiteEditorPanel />
    </div>
  );
}
